import { css } from '@emotion/css';
import { Theme, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { useStyles } from '@/hooks/useStyles';
import { Queries } from '@/queries/Queries';
import { toDateTime } from '@/utils/dateUtils';

import { Button } from '../../atoms/Button';
import { InformationPaper } from '../../atoms/InformationPaper';

export const NurseReinclusionNudge = ({ patientId }: { patientId: string }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const createReinclusionNudge =
    Queries.practitioner.useCreateNurseReinclusionSuggestion();

  const deleteNurseReinclusionRequestMutation =
    Queries.practitioner.useDeleteNurseReinclusionSuggestion();

  const { data: nurseReinclusionNudge } =
    Queries.practitioner.useGetNurseReinclusionSuggestion(patientId);

  if (!nurseReinclusionNudge?.should_ask_for_nudge) {
    return null;
  }

  if (nurseReinclusionNudge.reinclusion_request_active) {
    return (
      <InformationPaper
        className={styles.container}
        isSquare
        variant="outlined"
      >
        <div className={styles.col}>
          <Typography variant="subtitle">
            {t(
              'patientNavigation.reinclusionNudge.titleReinclusionAlreadyCreated',
            )}
          </Typography>
          <Typography variant="body">
            {t('patientNavigation.reinclusionNudge.subtitle')}{' '}
            {toDateTime(nurseReinclusionNudge.last_cp_end).toLocaleString(
              DateTime.DATE_SHORT,
            )}
          </Typography>
        </div>
        <div className={styles.ctas}>
          <Button
            id="cancel-reinclusion-request"
            variant="outlined"
            pending={deleteNurseReinclusionRequestMutation.isPending}
            onClick={() =>
              deleteNurseReinclusionRequestMutation.mutate(patientId)
            }
          >
            {t('patientNavigation.reinclusionNudge.nurseCTACancel')}
          </Button>
        </div>
      </InformationPaper>
    );
  }

  return (
    <InformationPaper className={styles.container} isSquare variant="outlined">
      <div className={styles.col}>
        <Typography variant="subtitle">
          {t('patientNavigation.reinclusionNudge.title')}
        </Typography>
        <Typography variant="body">
          {t('patientNavigation.reinclusionNudge.subtitle')}{' '}
          {toDateTime(nurseReinclusionNudge.last_cp_end).toLocaleString(
            DateTime.DATE_SHORT,
          )}
        </Typography>
      </div>
      <div className={styles.ctas}>
        <Button
          id="create-reinclusion-request"
          variant="contained"
          pending={createReinclusionNudge.isPending}
          onClick={() => createReinclusionNudge.mutate(patientId)}
        >
          {t('patientNavigation.reinclusionNudge.nurseCTACreate')}
        </Button>
      </div>
    </InformationPaper>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: ${theme.spacing(8, 12)};
  `,
  ctas: css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(4)};
  `,
  col: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
  `,
});
